import { Button, Typography, Paper, Link, LinearProgress, TextField } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import React from "react";

import { getStaticUrl, isElectron } from "js/config";
import BeautifulLogo from "js/react/components/BeautifulLogo";
import Container from "js/react/components/Container";
import LabelledDivider from "js/react/components/LabelledDivider";
import { dialogTheme } from "js/react/materialThemeOverrides";

const googleIcon = getStaticUrl("/images/auth/google.svg");
const spacing = 12;

const styles = theme => {
    return {
        // sso
        ssoNotice: {
            borderRadius: 4,
            padding: 6,
            textAlign: "center",
            color: "#46555a",
            fontSize: 13
        },
        ssoIcon: {
            fontFamily: "Material Icons",
            fontSize: 20,
            fontStyle: "normal",
            verticalAlign: "bottom"
        },
        // google sign-in
        googleButton: {
            "& img": {
                width: 18,
                height: 18,
                paddingRight: ".5em"
            }
        },
        // rows
        smallRow: {
            marginTop: spacing,
            marginBottom: spacing,
            "&:first-child": {
                marginTop: 0
            }
        },
        largeRow: {
            marginTop: spacing * 2,
            marginBottom: spacing * 2,
        },
        // error message
        error: {
            background: "rgb(247, 181, 174)",
            color: "rgb(97, 26, 21)",
            paddingTop: spacing * 1,
            paddingBottom: spacing * 1,
            paddingLeft: spacing * 3,
            paddingRight: spacing * 3,
        },
        // info message
        info: {
            background: "#d1eeff",
            fontSize: 14,
            paddingTop: spacing * 1,
            paddingBottom: spacing * 1,
            paddingLeft: spacing * 2,
            paddingRight: spacing * 2,
        },
        // AuthPage
        authPage: {
            marginBottom: spacing * 5
        },
        logoContainer: {
            textAlign: "center",
            padding: spacing * 4,
        },
        // AuthForm
        authFormContent: {
            padding: spacing * 2
        },
        // text
        muted: {
            color: "#ddd"
        },
        wideSpace: {
            paddingLeft: ".2em"
        },
        // text field
        disabledField: {
            "& input": {
                background: "#efefef",
                color: "black !important"
            }
        }
    };
};

const AuthPage = withStyles(styles)(
    ({ classes, children }) => (
        <MuiThemeProvider theme={dialogTheme}>
            <div className={classes.authPage}>
                <div className={classes.logoContainer}>
                    <BeautifulLogo noLink={isElectron} forceMarketing />
                </div>
                <Container>
                    {children}
                </Container>
            </div>
        </MuiThemeProvider>
    )
);

const AuthForm = withStyles(styles)(
    ({ classes, title, children, error, loading, info }) => (
        <Paper style={{ overflow: "hidden" }}>
            {loading && <LinearProgress />}
            {error && <div data-cy="error" className={classes.error}>{error}</div>}
            {info && <div data-cy="info" className={classes.info}>{info}</div>}
            <div className={classes.authFormContent}>
                {/* TODO: what should noValidate and autoComplete be set to? */}
                <form noValidate autoComplete="off">
                    <Typography variant="h6" component="h1" gutterBottom>
                        {title}
                    </Typography>
                    <div>
                        {React.Children.map(children, child => (
                            <div className={classes.smallRow}>
                                {child}
                            </div>
                        ))}
                    </div>
                </form>
            </div>
        </Paper>
    )
);

const GoogleButton = withStyles(styles)(
    ({ classes, label, disabled, onClick }) => (
        <>
            <div className={classes.largeRow}>
                <Button
                    id="google"
                    className={classes.googleButton}
                    variant="outlined"
                    onClick={onClick}
                    disabled={disabled}
                    fullWidth
                >
                    <img src={googleIcon} /> {label}
                </Button>
            </div>
        </>
    )
);

const SignInOrCreateAccount = withStyles(styles)(
    class SignInOrCreateAccount extends React.Component {
        emailInput = React.createRef();
        passwordInput = React.createRef();

        handleClickSubmit = e => {
            e.preventDefault();
            this.props.onClickSubmit({
                email: this.emailInput.current.value,
                password: this.passwordInput.current ? this.passwordInput.current.value : ""
            });
        }

        handleClickGoogleButton = () => {
            this.props.onClickGoogleButton();
        }

        handleClickSwitchLink = e => {
            e.preventDefault();
            this.props.onClickSwitchLink({ signIn: !!this.props.createAccount });
        }

        handleClickForgot = e => {
            e.preventDefault();
            this.props.onClickForgot({
                email: this.emailInput.current.value
            });
        }

        handleClickSSOLink = e => {
            e.preventDefault();
            this.props.onClickSSOLink();
        }

        render = () => {
            const {
                classes,
                email, error, loading, createAccount, emailError, passwordError,
                onClickSSOLink,
                showOnlyGoogle,
                noToggle,
                ssoState,
                children,
                info
            } = this.props;

            const sso = ssoState || "none";
            const signInLabel = (sso == "strict" || sso == "optional") ? "Continue" : "Sign in";
            const onEmailChange = this.props.onEmailChange || (() => {});

            return (
                <AuthPage>
                    <AuthForm
                        title={createAccount
                            ? "Create an account"
                            : "Sign in to your account"
                        }
                        error={error}
                        info={info}
                        loading={loading}
                    >
                        {sso !== "strict" && (
                            <GoogleButton
                                label={createAccount
                                    ? "Sign up with Google"
                                    : "Sign in with Google"
                                }
                                onClick={this.handleClickGoogleButton}
                                disabled={loading}
                            />
                        )}
                        {children}
                        {!showOnlyGoogle && (
                            <>
                                {sso !== "strict" && (
                                    <div style={{ marginBottom: -spacing }}>
                                        <LabelledDivider label="OR" />
                                    </div>
                                )}
                                <div className={classes.smallRow}>
                                    <TextField
                                        id="email"
                                        className={email ? classes.disabledField : ""}
                                        variant="outlined"
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        autoFocus={!email}
                                        inputRef={this.emailInput}
                                        disabled={!!email}
                                        fullWidth
                                        helperText={emailError}
                                        error={!!emailError}
                                        onChange={onEmailChange}
                                    />
                                </div>
                                {sso !== "strict" && (
                                    <div className={classes.smallRow}>
                                        <TextField
                                            variant="outlined"
                                            type="password"
                                            placeholder={createAccount
                                                ? "Create Password"
                                                : "Password"
                                            }
                                            autoFocus={!!email}
                                            inputRef={this.passwordInput}
                                            fullWidth
                                            helperText={passwordError}
                                            error={!!passwordError}
                                        />
                                    </div>
                                )}
                                {(sso === "strict" || sso === "optional") && (
                                    <div className={classes.smallRow}>
                                        <div className={classes.ssoNotice}>
                                            <i className={classes.ssoIcon}>lock</i>
                                            Single sign-on enabled
                                        </div>
                                    </div>
                                )}
                                <div className={classes.smallRow}>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        variant="contained"
                                        onClick={this.handleClickSubmit}
                                        // TODO: handle enter key press -> submit
                                        disabled={loading}
                                        fullWidth
                                    >{createAccount ? "Create account" : signInLabel}
                                    </Button>
                                </div>
                            </>
                        )}
                        {createAccount && (
                            <Typography variant="body2" style={{
                                color: "#777",
                                marginBottom: -spacing
                            }}>
                                By creating an account, you agree to the {}
                                <Link
                                    href="/terms-of-service"
                                    target="_blank"
                                >Terms of Service
                                </Link> and {}
                                <Link
                                    href="/privacy-policy"
                                    target="_blank"
                                >Privacy Policy
                                </Link>.
                            </Typography>
                        )}
                        {!createAccount && !showOnlyGoogle && sso !== "strict" && (
                            <Typography variant="body2">
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={this.handleClickForgot}
                                >Forgot your password?</Link>
                            </Typography>
                        )}
                    </AuthForm>
                    <div className={classes.smallRow}>
                        <Typography variant="body1" className={classes.muted}>
                            {!noToggle && <>
                                {createAccount
                                    ? "Already have an account?"
                                    : "New to beautiful.ai?"
                                } {}
                                <Link
                                    id="toggle"
                                    href="#"
                                    className={classes.wideSpace}
                                    onClick={this.handleClickSwitchLink}
                                >{createAccount ? "Sign in" : "Create an account"}
                                </Link>
                            </>}
                            {!createAccount && onClickSSOLink && (
                                <span style={{ float: "right" }}>
                                    <Link
                                        href="#"
                                        className={classes.wideSpace}
                                        onClick={this.handleClickSSOLink}
                                    >Sign in with SSO
                                    </Link>
                                </span>
                            )}
                        </Typography>
                    </div>
                </AuthPage>
            );
        }
    }
);

export const SignIn = props => (
    <SignInOrCreateAccount {...props} />
);

export const CreateAccount = props => (
    <SignInOrCreateAccount {...props} createAccount />
);

export class ResetPassword extends React.Component {
    emailInput = React.createRef();

    handleClickReset = () => {
        this.props.onClickReset({
            email: this.emailInput.current.value
        });
    }

    render = () => {
        const { loading, error, defaultEmail, emailError } = this.props;
        return (
            <AuthPage>
                <AuthForm
                    title="Forgot your password?"
                    loading={loading}
                    error={error}
                >
                    <Typography variant="body1">
                        Enter your email address to reset your password.
                    </Typography>
                    <TextField
                        variant="outlined"
                        type="email"
                        placeholder="Email"
                        defaultValue={defaultEmail}
                        inputRef={this.emailInput}
                        fullWidth
                        autoFocus
                        helperText={emailError}
                        error={!!emailError}
                    />
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={this.handleClickReset}
                        fullWidth
                    >Reset Password
                    </Button>
                </AuthForm>
            </AuthPage>
        );
    }
}

export class ResetDone extends React.Component {
    render = () => {
        return (
            <AuthPage>
                <AuthForm
                    title="Forgot your password?"
                >
                    <Typography variant="body1">
                        Success! We've sent you a password reset email.
                    </Typography>
                </AuthForm>
            </AuthPage>
        );
    }
}

export class WaitForGoogle extends React.Component {
    render = () => {
        return (
            <AuthPage>
                <AuthForm
                    title="Waiting for Google..."
                    loading={false}
                    error={false}
                >
                </AuthForm>
            </AuthPage>
        );
    }
}
